import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import "./BannerManagement.css";

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: null,
  });
  const [editingBanner, setEditingBanner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isFormVisible, setIsFormVisible] = useState(false);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await axios.get("https://api.swasthmitra.com/api/banners");
      setBanners(response.data);
    } catch (error) {
      setError("Failed to fetch banners");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      image: e.target.files[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const formDataToSend = new FormData();
    formDataToSend.append("title", formData.title);
    formDataToSend.append("description", formData.description);
    if (formData.image) {
      formDataToSend.append("image", formData.image);
    }

    try {
      if (editingBanner) {
        await axios.put(
          `https://api.swasthmitra.com/api/banners/${editingBanner._id}`,
          formDataToSend
        );
      } else {
        await axios.post("https://api.swasthmitra.com/api/banners", formDataToSend);
      }
      fetchBanners();
      resetForm();
    } catch (error) {
      setError(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title,
      description: banner.description,
      image: null,
    });
    setIsFormVisible(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this banner?")) {
      try {
        await axios.delete(`https://api.swasthmitra.com/api/banners/${id}`);
        fetchBanners();
      } catch (error) {
        setError("Failed to delete banner");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      image: null,
    });
    setEditingBanner(null);
    setIsFormVisible(false);
  };

  return (
    <div className="banner-management">
      <h2>Banner Management</h2>

      <button
        onClick={() => setIsFormVisible(true)}
        className="form-actions button"
        style={{ marginBottom: "20px" }}
      >
        Add New Banner
      </button>

      {isFormVisible && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) resetForm();
          }}
        >
          <motion.form
            onSubmit={handleSubmit}
            className="banner-form"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="close-button" onClick={resetForm}>
              ×
            </button>

            <h3>{editingBanner ? "Edit Banner" : "Add New Banner"}</h3>

            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label>Title:</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Description:</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Image:</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                required={!editingBanner}
              />
            </div>

            <div className="form-actions">
              <button type="submit" disabled={loading}>
                {loading
                  ? "Processing..."
                  : editingBanner
                  ? "Update Banner"
                  : "Add Banner"}
              </button>
              <button type="button" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </motion.form>
        </motion.div>
      )}

      <div className="banners-list">
        <h3>Existing Banners</h3>
        <div className="banners-grid">
          {banners.map((banner) => (
            <motion.div
              key={banner._id}
              className="banner-card"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <img src={banner.image} alt={banner.title} />
              <h4>{banner.title}</h4>
              <p>{banner.description}</p>
              <div className="banner-actions">
                <button onClick={() => handleEdit(banner)}>Edit</button>
                <button onClick={() => handleDelete(banner._id)}>Delete</button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BannerManagement;
